import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AppLogo } from './AppLogo';
import { PlanScene } from './marketing/PlanScene';

interface PublicAccessShellProps {
  title: string;
  description: string;
  children: ReactNode;
  sideTitle: string;
  sideDescription: string;
  sideItems: string[];
  maxWidth?: 'md' | 'lg';
}

const widthClass = {
  md: 'max-w-md',
  lg: 'max-w-lg',
};

// ── Flat blueprint sheet aside illustration ────────────────────────────────────

function BlueprintAside({
  sideTitle,
  sideDescription,
  sideItems,
}: {
  sideTitle: string;
  sideDescription: string;
  sideItems: string[];
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--line-strong)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Title row */}
      <div
        style={{
          borderBottom: '1px solid var(--line)',
          padding: '16px 20px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <h2
            style={{
              fontWeight: 600,
              fontSize: 18,
              letterSpacing: '-0.01em',
              lineHeight: 1.1,
              color: 'var(--ink)',
            }}
          >
            {sideTitle}
          </h2>
          <p
            style={{
              fontSize: 13,
              color: 'var(--ink-soft)',
              marginTop: 6,
              lineHeight: 1.55,
            }}
          >
            {sideDescription}
          </p>
        </div>
      </div>

      {/* Rzut jako scena 3D; płaska ilustracja jest pierwszą klatką i wersją
          awaryjną (brak WebGL). Kolory z tokenów, więc działa w trybie ciemnym. */}
      <PlanScene
        className="min-h-[340px]"
        labels={['M-18 · zawilgocenie', 'M-19 · pęknięcie', 'M-20 · instalacja']}
        fallback={
          <div
            style={{
              position: 'relative',
              height: 280,
              background: 'var(--card)',
              backgroundImage:
                'linear-gradient(rgba(20,24,31,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(20,24,31,0.06) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
              borderBottom: '1px solid var(--line)',
              overflow: 'hidden',
            }}
            aria-hidden="true"
          >
            {/* Measurement ticks on left edge */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 10,
                background:
                  'repeating-linear-gradient(0deg, var(--ink) 0 1px, transparent 1px 22px)',
                opacity: 0.3,
              }}
            />

            {/* Outer walls */}
            <div
              style={{
                position: 'absolute',
                left: '8%',
                top: '10%',
                width: '84%',
                height: 2,
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '8%',
                top: '10%',
                width: 2,
                height: '76%',
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '8%',
                bottom: '14%',
                width: '84%',
                height: 2,
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '8%',
                top: '10%',
                width: 2,
                height: '76%',
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />
            {/* Interior partition */}
            <div
              style={{
                position: 'absolute',
                left: '42%',
                top: '10%',
                width: 2,
                height: '48%',
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '8%',
                top: '48%',
                width: '56%',
                height: 2,
                background: 'var(--ink)',
                opacity: 0.75,
              }}
            />

            {/* Rooms */}
            <div
              style={{
                position: 'absolute',
                left: '11%',
                top: '14%',
                width: '26%',
                height: '28%',
                border: '1.5px solid var(--line)',
                background: 'rgba(255,255,255,0.4)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '46%',
                top: '14%',
                width: '40%',
                height: '30%',
                border: '1.5px solid var(--line)',
                background: 'rgba(255,255,255,0.4)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '11%',
                top: '52%',
                width: '32%',
                height: '28%',
                border: '1.5px solid var(--line)',
                background: 'rgba(255,255,255,0.4)',
              }}
            />

            {/* Marker 18 — hi-vis orange */}
            <div
              style={{
                position: 'absolute',
                left: '20%',
                top: '24%',
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'var(--hi)',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontWeight: 600,
                fontSize: 12,
                border: '2px solid var(--card)',
                boxShadow: '0 4px 12px rgba(20,24,31,0.28)',
                zIndex: 3,
              }}
            >
              18
            </div>
            <div
              style={{
                position: 'absolute',
                left: '28%',
                top: '17%',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: '0.04em',
                color: 'var(--ink-soft)',
                background: 'var(--card)',
                padding: '2px 6px',
                border: '1px solid var(--line)',
                whiteSpace: 'nowrap',
                zIndex: 3,
                borderRadius: 2,
              }}
            >
              M-18 · zawilgocenie ↑
            </div>

            {/* Marker 19 — warn amber */}
            <div
              style={{
                position: 'absolute',
                left: '62%',
                top: '28%',
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'var(--warn)',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontWeight: 600,
                fontSize: 12,
                border: '2px solid var(--card)',
                boxShadow: '0 4px 12px rgba(20,24,31,0.28)',
                zIndex: 3,
              }}
            >
              19
            </div>
            <div
              style={{
                position: 'absolute',
                left: '66%',
                top: '40%',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: '0.04em',
                color: 'var(--ink-soft)',
                background: 'var(--card)',
                padding: '2px 6px',
                border: '1px solid var(--line)',
                whiteSpace: 'nowrap',
                zIndex: 3,
                borderRadius: 2,
              }}
            >
              M-19 · pęknięcie
            </div>

            {/* Marker 20 — info */}
            <div
              style={{
                position: 'absolute',
                left: '24%',
                top: '62%',
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'var(--ink)',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--card)',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontWeight: 600,
                fontSize: 12,
                border: '2px solid var(--card)',
                boxShadow: '0 4px 12px rgba(20,24,31,0.28)',
                zIndex: 3,
              }}
            >
              20
            </div>
            <div
              style={{
                position: 'absolute',
                left: '32%',
                top: '69%',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: '0.04em',
                color: 'var(--ink-soft)',
                background: 'var(--card)',
                padding: '2px 6px',
                border: '1px solid var(--line)',
                whiteSpace: 'nowrap',
                zIndex: 3,
                borderRadius: 2,
              }}
            >
              M-20 · instalacja
            </div>

            {/* Title block — bottom-right */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 190,
                border: '1.5px solid var(--ink)',
                background: 'var(--card)',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 10.5,
                zIndex: 4,
              }}
            >
              {(
                [
                  ['Projekt', 'Centrum Północ'],
                  ['Arkusz', 'A-01'],
                  ['Rew.', '03'],
                ] as [string, string][]
              ).map(([label, val]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {label}
                  </span>
                  <strong style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    {val}
                  </strong>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                }}
              >
                <span
                  style={{
                    color: 'var(--muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Sync
                </span>
                <strong
                  style={{
                    fontWeight: 600,
                    color: 'var(--ink)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'var(--ok)',
                      boxShadow: '0 0 0 3px var(--ok-bg)',
                    }}
                  />
                  offline · 3
                </strong>
              </div>
            </div>
          </div>
        }
      />

      {/* Side items */}
      {sideItems.length > 0 && (
        <div
          style={{
            padding: '14px 16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
          }}
        >
          {sideItems.map((item) => (
            <div
              key={item}
              style={{
                border: '1px solid var(--line)',
                background: 'var(--paper-2)',
                padding: '8px 10px',
                borderRadius: 'var(--r)',
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--ink-soft)',
                letterSpacing: '0.02em',
              }}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function PublicAccessShell({
  title,
  description,
  children,
  sideTitle,
  sideDescription,
  sideItems,
  maxWidth = 'md',
}: PublicAccessShellProps) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-paper text-ink">
      {/* Strony dostępu mają ten sam spokojny wygląd co aplikacja: bez siatki,
          bez linijki i bez grubych obrysów. Ilustracja rzutu z boku zostaje. */}
      {/* Header */}
      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between border-b border-line px-5 py-4 sm:px-8">
        <Link to="/" className="flex items-center gap-3">
          {/* Logo glyph: dark square with hi-vis dot — matches sidebar brand */}
          <span
            className="flex h-9 w-9 items-center justify-center"
            style={{
              background: 'var(--ink)',
              borderRadius: 6,
              position: 'relative',
            }}
          >
            <AppLogo className="h-5 w-5 text-card" />
          </span>
          <span>
            <span
              className="block font-sans font-semibold text-ink"
              style={{ fontSize: 15 }}
            >
              Pinveo
            </span>
            <span
              className="block font-mono text-muted"
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              dokumentacja terenowa
            </span>
          </span>
        </Link>

        <Link to="/" className="btn-secondary text-xs">
          ← Strona główna
        </Link>
      </header>

      {/* Main content: two-column layout */}
      <main className="relative z-10 mx-auto grid min-h-[calc(100vh-5.5rem)] max-w-7xl gap-10 px-5 pb-12 pt-8 sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:pb-20">
        {/* Form section */}
        <section
          className={`public-form mx-auto w-full ${widthClass[maxWidth]} lg:order-2 lg:mx-0 lg:justify-self-end`}
        >
          {/* Flat blueprint card */}
          <div
            style={{
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '28px 28px 24px',
            }}
          >
            <h1
              className="font-sans font-semibold text-ink"
              style={{
                fontSize: 24,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
                marginBottom: 8,
              }}
            >
              {title}
            </h1>
            <p
              className="text-ink-soft"
              style={{ fontSize: 14, lineHeight: 1.65, marginBottom: 24 }}
            >
              {description}
            </p>

            {/* Ruled line between header and form */}
            <div
              style={{
                height: 1,
                background: 'var(--line)',
                marginBottom: 24,
              }}
            />

            {children}
          </div>
        </section>

        {/* Side blueprint illustration */}
        <aside className="hidden lg:order-1 lg:block">
          <BlueprintAside
            sideTitle={sideTitle}
            sideDescription={sideDescription}
            sideItems={sideItems}
          />
        </aside>
      </main>
    </div>
  );
}
